import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, GraduationCap, Monitor, UserCog } from "lucide-react";
import { AppCard, AppCardContent, StatusBadge } from "../../ui";
import { formatDate } from "../../../utils/format";
import { batchDates, batchSchedule, contentExpiry } from "./batchDetails";

/** One fact in the meta line: a glyph and a short phrase. */
const Fact = ({ icon: Icon, children }) => (
  <li className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5">
    <Icon size={14} className="tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />
    <span className="tw-truncate">{children}</span>
  </li>
);

/**
 * BatchIdentityCard — which batch this is, its state, and the page's actions.
 *
 * It decides nothing about permissions: the page builds `actions` from what the
 * caller may do and passes them in.
 *
 * Props:
 *   batch       — the `GET /batches/:id` batch
 *   instructors — batchDetails.batchInstructors()
 *   actions     — the buttons and menu on the right
 */
const BatchIdentityCard = ({ batch, instructors = [], actions }) => {
  const expiry = contentExpiry(batch);
  const schedule = batchSchedule(batch);
  const dates = batchDates(batch);
  const course = batch.course && typeof batch.course === "object" ? batch.course : null;

  return (
    <AppCard as="section" aria-label="Batch summary">
      <AppCardContent className="tw-flex tw-flex-col tw-gap-4 min-[1200px]:tw-flex-row min-[1200px]:tw-items-start">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <h2 className="tw-m-0 tw-min-w-0 tw-truncate tw-text-[1.375rem] tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-content">
              {batch.name}
            </h2>
            <StatusBadge status={String(batch.status || "upcoming").toLowerCase()} domain="batch" />
            {batch.isLive && <StatusBadge status="live" domain="batch">Live class now</StatusBadge>}
            {expiry?.enforced && (
              <StatusBadge status={expiry.expired ? "danger" : "warning"}>
                {expiry.expired ? "Content expired" : `Content expires ${formatDate(expiry.date)}`}
              </StatusBadge>
            )}
          </div>

          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-p-0 tw-text-[0.8125rem] tw-text-content-muted">
            {course && (
              <li className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5">
                <GraduationCap size={14} className="tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />
                <Link
                  to={`/courses/${course._id}`}
                  className="tw-truncate tw-rounded-sm tw-text-content-muted tw-no-underline tw-focus-ring hover:tw-text-primary hover:tw-underline"
                >
                  {course.name}
                </Link>
              </li>
            )}
            {instructors.length > 0 && (
              <Fact icon={UserCog}>{instructors.map((i) => i.name).filter(Boolean).join(", ")}</Fact>
            )}
            {dates && <Fact icon={CalendarDays}>{dates}</Fact>}
            {schedule && <Fact icon={Clock}>{schedule}</Fact>}
            {batch.mode && <Fact icon={Monitor}>{batch.mode}</Fact>}
          </ul>
        </div>

        {actions && (
          <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">
            {actions}
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default BatchIdentityCard;
